<template>
  <div :class="$options.name">
    <v-toolbar dark color="blue darken-3" elevation="0" flat>
      <div v-if="$slots['toolbar-left-actions']" class="mr-2">
        <slot name="toolbar-left-actions" />
      </div>

      <v-btn
        class="mr-4 primary"
        tile
        elevation="0"
        :disabled="loading"
        @click="doUpdate"
      >
        Обновить
      </v-btn>

      <slot name="toolbar-center-actions" />

      <v-text-field
        v-model="query"
        class="ml-2"
        append-icon="search"
        label="Поиск по имени файла, тексту статуса, имени и фамилии пользователя"
        single-line
        hide-details
        @input="doSearch"
      />

      <slot name="toolbar-right-actions" />
    </v-toolbar>

    <v-data-table
      hide-default-footer
      multi-sort
      no-data-text="Ничего нет"
      :server-items-length="Infinity"
      :headers="headers"
      :items="formattedItems"
      :loading="loading"
      @update:sort-by="onChangeSortBy"
      @update:sort-desc="onChangeSortDesc"
    >
      <template #footer>
        <v-pagination
          v-if="pagination"
          color="blue darken-3"
          class="pb-4 pt-4"
          :length="pagination.lastPage"
          :total-visible="8"
          :value="pagination.currentPage"
          :disabled="pagination.lastPage === 1 || loading"
          @input="onChangePage"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>

import debounce from 'lodash/debounce';
import moment from 'moment-timezone';

const defaultHeaders = [
  { text: 'Файл', value: 'originalFileName' },
  { text: 'Время запуска', value: 'startedAt' },
  { text: 'Пользователь', value: 'userName' },
  { text: 'Импортировано элементов', value: 'importedItemsCount' },
  { text: 'Статус', value: 'status', sortable: false },
  { text: 'Расшифровка статуса', value: 'statusText' },
];

export default {
  name: 'import-data-table',

  props: {
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },

    items: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    pagination: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      query: '',
      sortBy: [],
      sortDesc: [],
    };
  },

  computed: {
    formattedItems() {
      return this.items.map((item) => ({
        ...item,
        status: this.humanizeStatus(item.status),
        startedAt: item.startedAt?.date
          ? moment.utc(item.startedAt.date).local().format('DD.MM.YYYY HH:mm')
          : '-',
        userName: item.createdBy?.name,
      }));
    },
  },

  methods: {
    onChangeSortBy(value) {
      this.sortBy = value;

      this.onChangeSort();
    },

    onChangeSortDesc(value) {
      this.sortDesc = value;

      this.onChangeSort();
    },

    // eslint-disable-next-line func-names
    onChangeSort: debounce(function () {
      this.doReload(this.pagination.currentPage ?? 1, this.sortBy, this.sortDesc, this.query);
    }, 50),

    // eslint-disable-next-line func-names
    doSearch: debounce(function () {
      this.doReload(1, this.sortBy, this.sortDesc, this.query);
    }, 750),

    doUpdate() {
      this.doReload(this.pagination.currentPage ?? 1, this.sortBy, this.sortDesc, this.query);
    },

    onChangePage(page) {
      this.doReload(page, this.sortBy, this.sortDesc, this.query);
    },

    doReload(page, sortBy, sortDesc, query) {
      this.$emit('onDoReload', {
        page,
        sortBy,
        sortDesc,
        query,
      });
    },

    humanizeStatus(status = null) {
      const unknown = 'Не известно';

      const statusesMap = {
        pending: 'В ожидании',
        queued: 'В очереди',
        started: 'Запущен',
        success: 'Успешно завершен',
        failed: 'Произошла ошибка',
      };

      return statusesMap[status] ?? unknown;
    },
  },
};

</script>

<style lang="scss" scoped>
  >>> .data-import-dialog {
    margin-left: 0 !important;
  }
</style>
